import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './ui/views/App.tsx'
import { registerSW } from 'virtual:pwa-register'
import { localDB } from './db/client'
import { initSentry } from './core/utils/sentry'
import { initializeServices } from './core/services'
import { ServicesProvider } from './core/services/ServicesContext'
import { serverSyncService } from './core/infrastructure/server-sync-service'
import { scenarioHydrationCoordinator } from './core/coordination/scenario-hydration-coordinator'
import { memoryStateService } from './core/state/memory-store'
import { connectionStateService } from './core/infrastructure/connection-state-service'
import { initThemePreference } from './core/utils/theme'
import { requestPersistentStorage } from './core/utils/storage'
import { pwaUpdateStore } from './ui/notifications/pwa-update-store'

// Initialize Sentry for error tracking
initSentry()

// Ask the browser to protect local data (IndexedDB, caches) from eviction.
// Important for the offline-first experience, especially in the Android (TWA) app.
requestPersistentStorage()

// Initialize theme early to avoid flash on load
initThemePreference()

// Initialize all core services
initializeServices().catch(err => 
  console.error('Failed to initialize services:', err)
)

// Initialize localDB early from cached auth (for page refreshes)
const cachedUserRaw = localStorage.getItem('auth_user')
if (cachedUserRaw) {
  try {
    const cachedUser = JSON.parse(cachedUserRaw)
    if (cachedUser?.userId) {
      localDB.init(cachedUser.userId).catch(err => 
        console.error('Failed to initialize localDB on startup:', err)
      )
    }
  } catch {}
}

// Store interval ID for cleanup
let updateCheckIntervalId: number | undefined
let removeUpdateListeners: (() => void) | undefined

// Track page load time to detect app reopens with pending updates
const pageLoadTime = Date.now()
const AUTO_APPLY_WINDOW_MS = 5000

// Register service worker for PWA
const updateSW = registerSW({
  onNeedRefresh() {
    // If a new SW is already waiting when the app opens (within a few
    // seconds of page load), the user closed and reopened the app while
    // an update was pending — apply it silently instead of showing the banner.
    if (Date.now() - pageLoadTime < AUTO_APPLY_WINDOW_MS) {
      updateSW(true)
      return
    }
    // Otherwise show a non-blocking update banner so the user can
    // finish what they are doing before reloading.
    pwaUpdateStore.setNeedsRefresh(() => updateSW(true))
  },
  onOfflineReady() {
    console.log('App ready to work offline')
  },
  onRegisteredSW(_swScriptUrl, registration) {
    // Android/Chrome can take minutes to re-check for SW updates.
    // Proactively request updates when the app becomes active again.
    if (registration) {
      // Clear any existing interval
      if (updateCheckIntervalId !== undefined) {
        clearInterval(updateCheckIntervalId)
      }

      // Remove previous listeners (hot reload / re-register scenarios)
      if (removeUpdateListeners) {
        removeUpdateListeners()
        removeUpdateListeners = undefined
      }

      const requestUpdate = () => {
        registration.update().catch(err =>
          console.debug('Service worker update check failed:', err)
        )
      }

      // Check immediately after registration
      requestUpdate()

      const onVisibilityChange = () => {
        if (!document.hidden) requestUpdate()
      }
      const onFocus = () => requestUpdate()
      const onOnline = () => requestUpdate()

      document.addEventListener('visibilitychange', onVisibilityChange)
      window.addEventListener('focus', onFocus)
      window.addEventListener('online', onOnline)

      removeUpdateListeners = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('focus', onFocus)
        window.removeEventListener('online', onOnline)
      }
      
      // Periodic checks (only while visible) to reduce "stuck on old build" time.
      updateCheckIntervalId = setInterval(() => {
        if (!document.hidden) requestUpdate()
      }, 60 * 1000) as unknown as number
    }
  },
})

// Clean up interval on page unload
window.addEventListener('unload', () => {
  if (updateCheckIntervalId !== undefined) {
    clearInterval(updateCheckIntervalId)
  }

  if (removeUpdateListeners) {
    removeUpdateListeners()
    removeUpdateListeners = undefined
  }
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ServicesProvider
      services={{
        serverSync: serverSyncService,
        hydrationCoordinator: scenarioHydrationCoordinator,
        memoryState: memoryStateService,
        connectionState: connectionStateService
      }}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ServicesProvider>
  </StrictMode>,
)
